/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/react'
import dayjs, { Dayjs } from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { font, weightMedium } from 'src/components/css'

interface TimePickerWithIntervalProps {
  value?: Dayjs | null
  onChange?: (value: Dayjs | null) => void
  placeholder?: string
  disabled?: boolean
  style?: React.CSSProperties
  className?: string
  popupZIndex?: number
  id?: string
  /** When set, dropdown options show duration relative to this time (e.g., "1:30pm (1 hr)") */
  relativeToTime?: Dayjs | null
}

interface TimeOption {
  value: Dayjs
  label: string
}

/**
 * Parse free-form time input into a Dayjs object.
 * Supports: "2pm", "2:30pm", "2:30 PM", "14:30", "1430", etc.
 */
function parseTimeInput(text: string): Dayjs | null {
  const cleaned = text.trim().toLowerCase().replace(/\s+/g, '')
  if (!cleaned) {
    return null
  }

  let hours: number | null = null
  let minutes = 0

  // "2:30pm", "2:30am", "12:05pm"
  const match12hm = cleaned.match(/^(\d{1,2}):(\d{2})(am|pm)$/)
  if (match12hm) {
    hours = parseInt(match12hm[1], 10)
    minutes = parseInt(match12hm[2], 10)
    const period = match12hm[3]
    if (hours < 1 || hours > 12 || minutes > 59) {
      return null
    }
    if (period === 'am') {
      hours = hours === 12 ? 0 : hours
    } else {
      hours = hours === 12 ? 12 : hours + 12
    }
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
  }

  // "2pm", "12am"
  const match12h = cleaned.match(/^(\d{1,2})(am|pm)$/)
  if (match12h) {
    hours = parseInt(match12h[1], 10)
    const period = match12h[2]
    if (hours < 1 || hours > 12) {
      return null
    }
    if (period === 'am') {
      hours = hours === 12 ? 0 : hours
    } else {
      hours = hours === 12 ? 12 : hours + 12
    }
    return dayjs().hour(hours).minute(0).second(0).millisecond(0)
  }

  // "14:30" (24h)
  const match24h = cleaned.match(/^(\d{1,2}):(\d{2})$/)
  if (match24h) {
    hours = parseInt(match24h[1], 10)
    minutes = parseInt(match24h[2], 10)
    if (hours > 23 || minutes > 59) {
      return null
    }
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
  }

  // "1430" (military, 4 digits only)
  const matchMilitary = cleaned.match(/^(\d{2})(\d{2})$/)
  if (matchMilitary) {
    hours = parseInt(matchMilitary[1], 10)
    minutes = parseInt(matchMilitary[2], 10)
    if (hours > 23 || minutes > 59) {
      return null
    }
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
  }

  return null
}

function formatDisplayTime(value: Dayjs | null | undefined): string {
  if (!value || !value.isValid()) {
    return ''
  }
  return value.format('h:mma')
}

const INTERVAL_MINUTES = 15

function formatDuration(minutes: number): string {
  if (minutes <= 0) {
    return ''
  }
  if (minutes < 60) {
    return `${minutes} mins`
  }
  const hrs = minutes / 60
  if (hrs === 1) {
    return '1 hr'
  }
  if (Number.isInteger(hrs)) {
    return `${hrs} hrs`
  }
  return `${hrs} hrs`
}

const generateTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += INTERVAL_MINUTES) {
      const d = dayjs().hour(h).minute(m).second(0).millisecond(0)
      options.push({
        value: d,
        label: d.format('h:mma'),
      })
    }
  }
  return options
}

const ALL_TIME_OPTIONS = generateTimeOptions()

function generateRelativeOptions(startTime: Dayjs): TimeOption[] {
  const startMinutes = startTime.hour() * 60 + startTime.minute()
  const options: TimeOption[] = []
  for (let i = 1; i <= 96; i++) {
    const totalMinutes = startMinutes + i * INTERVAL_MINUTES
    const h = Math.floor(totalMinutes / 60) % 24
    const m = totalMinutes % 60
    const d = dayjs().hour(h).minute(m).second(0).millisecond(0)
    const durationMins = i * INTERVAL_MINUTES
    const durationLabel = formatDuration(durationMins)
    options.push({
      value: d,
      label: `${d.format('h:mma')} (${durationLabel})`,
    })
  }
  return options
}

function findNearestOptionIndex(
  value: Dayjs | null | undefined,
  options: TimeOption[]
): number {
  if (!value || !value.isValid() || options.length === 0) {
    // For relative options, start at beginning; for absolute, ~8:00am
    return options.length === 96 ? 32 : 0
  }
  const totalMinutes = value.hour() * 60 + value.minute()
  let bestIdx = 0
  let bestDiff = Infinity
  for (let i = 0; i < options.length; i++) {
    const optMinutes = options[i].value.hour() * 60 + options[i].value.minute()
    const diff = Math.abs(optMinutes - totalMinutes)
    if (diff < bestDiff) {
      bestDiff = diff
      bestIdx = i
    }
  }
  return bestIdx
}

function isTimeEqual(a: Dayjs, b: Dayjs | null | undefined): boolean {
  if (!b || !b.isValid()) {
    return false
  }
  return a.hour() === b.hour() && a.minute() === b.minute()
}

// ─── Styles ─────────────────────────────────────────

const inputStyles = css`
  height: 36px;
  background-color: ${'#212935'};
  border: 1px solid ${'#313b4b'};
  border-radius: 8px;
  padding: 0 12px;
  color: ${colors.NEUTRAL_0};
  ${font.body}
  font-size: 14px;
  ${weightMedium}
  width: 100%;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    border-color: ${colors.PRIMARY};
  }

  &:focus {
    border-color: ${colors.PRIMARY};
    box-shadow: 0 0 0 2px ${colors.PRIMARY}33;
    cursor: text;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.NEUTRAL_50};
  }
`

const dropdownStyles = css`
  position: fixed;
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.SECONDARY_100};
    border-radius: 3px;
  }
`

const optionBaseStyles = css`
  padding: 8px 12px;
  color: ${colors.NEUTRAL_0};
  ${font.body}
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.1s;

  &:hover {
    background-color: ${colors.SECONDARY_400};
  }
`

const optionSelectedStyles = css`
  background-color: ${colors.PRIMARY} !important;
  color: ${colors.SECONDARY_300} !important;
  font-weight: 600;
`

const optionHighlightedStyles = css`
  background-color: ${colors.SECONDARY_400};
`

// ─── Component ──────────────────────────────────────

const TimePickerWithInterval: React.FC<TimePickerWithIntervalProps> = ({
  value,
  onChange,
  placeholder = 'Select time',
  disabled = false,
  style,
  className,
  popupZIndex = 10002,
  id,
  relativeToTime,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputText, setInputText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 140,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLDivElement>(null)
  const justSelectedRef = useRef(false)
  const hasTypedRef = useRef(false)

  // Base options: relative (with durations) or absolute
  const baseOptions = useMemo(() => {
    if (relativeToTime && relativeToTime.isValid()) {
      return generateRelativeOptions(relativeToTime)
    }
    return ALL_TIME_OPTIONS
  }, [relativeToTime])

  // Filtered options based on typing (only filter when user has actually typed)
  const filteredOptions = useMemo(() => {
    if (!hasTypedRef.current || !inputText.trim()) {
      return baseOptions
    }
    const search = inputText.trim().toLowerCase().replace(/\s+/g, '')
    return baseOptions.filter((opt) => opt.label.toLowerCase().includes(search))
  }, [inputText, baseOptions])

  // Display value in the input
  const displayValue = isEditing ? inputText : formatDisplayTime(value)

  // Position the dropdown
  const updatePosition = useCallback(() => {
    if (!inputRef.current) {
      return
    }
    const rect = inputRef.current.getBoundingClientRect()
    const dropdownHeight = 300
    const spaceBelow = window.innerHeight - rect.bottom
    const flipAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

    setDropdownPos({
      top: flipAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 140),
    })
  }, [])

  // Open dropdown
  const openDropdown = useCallback(() => {
    if (disabled) {
      return
    }
    updatePosition()
    setIsOpen(true)
    const nearestIdx = findNearestOptionIndex(value, baseOptions)
    setHighlightedIndex(nearestIdx)
  }, [disabled, updatePosition, value, baseOptions])

  // Close dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setHighlightedIndex(-1)
  }, [])

  // Commit a value selection
  const commitValue = useCallback(
    (newValue: Dayjs | null) => {
      hasTypedRef.current = false
      onChange?.(newValue)
      setIsEditing(false)
      setInputText('')
      closeDropdown()
    },
    [onChange, closeDropdown]
  )

  // Select an option from the dropdown
  const selectOption = useCallback(
    (option: TimeOption) => {
      justSelectedRef.current = true
      commitValue(option.value)
      inputRef.current?.blur()
    },
    [commitValue]
  )

  // Parse and commit typed text
  const commitTypedText = useCallback(() => {
    if (!isEditing) {
      return
    }
    const parsed = parseTimeInput(inputText)
    if (parsed) {
      commitValue(parsed)
    } else {
      // Revert to current value
      setIsEditing(false)
      setInputText('')
    }
  }, [isEditing, inputText, commitValue])

  // Handle input focus
  const handleFocus = useCallback(() => {
    if (disabled) {
      return
    }
    hasTypedRef.current = false
    setIsEditing(true)
    setInputText(formatDisplayTime(value))
    openDropdown()
    // Select all text on focus
    setTimeout(() => {
      inputRef.current?.select()
    }, 0)
  }, [disabled, value, openDropdown])

  // Handle input blur
  const handleBlur = useCallback(() => {
    // Delay to allow dropdown clicks to register
    setTimeout(() => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false
        return
      }
      commitTypedText()
      closeDropdown()
    }, 150)
  }, [commitTypedText, closeDropdown])

  // Handle typing
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      hasTypedRef.current = true
      setInputText(e.target.value)
      setIsEditing(true)
      if (!isOpen) {
        openDropdown()
      }
      setHighlightedIndex(0)
    },
    [isOpen, openDropdown]
  )

  // Handle keyboard
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!isOpen) {
          openDropdown()
          return
        }
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!isOpen) {
          openDropdown()
          return
        }
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (
          isOpen &&
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          selectOption(filteredOptions[highlightedIndex])
        } else {
          commitTypedText()
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsEditing(false)
        setInputText('')
        closeDropdown()
        inputRef.current?.blur()
      } else if (e.key === 'Tab') {
        commitTypedText()
        closeDropdown()
      }
    },
    [
      isOpen,
      openDropdown,
      closeDropdown,
      highlightedIndex,
      filteredOptions,
      selectOption,
      commitTypedText,
    ]
  )

  // Auto-scroll to selected/highlighted item when dropdown opens
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      // Use requestAnimationFrame to wait for dropdown render
      requestAnimationFrame(() => {
        if (selectedItemRef.current && dropdownRef.current) {
          const container = dropdownRef.current
          const item = selectedItemRef.current
          container.scrollTop =
            item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2
        }
      })
    }
  }, [isOpen])

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && highlightedIndex >= 0) {
      const container = dropdownRef.current
      const items = container.querySelectorAll('[data-time-option]')
      const item = items[highlightedIndex] as HTMLElement | undefined
      if (item) {
        const containerRect = container.getBoundingClientRect()
        const itemRect = item.getBoundingClientRect()
        if (itemRect.bottom > containerRect.bottom) {
          item.scrollIntoView({ block: 'nearest' })
        } else if (itemRect.top < containerRect.top) {
          item.scrollIntoView({ block: 'nearest' })
        }
      }
    }
  }, [isOpen, highlightedIndex])

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handleReposition = () => updatePosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, updatePosition])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        inputRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return
      }
      commitTypedText()
      closeDropdown()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isOpen, commitTypedText, closeDropdown])

  const dropdown =
    isOpen &&
    createPortal(
      <div
        ref={dropdownRef}
        css={dropdownStyles}
        style={{
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          zIndex: popupZIndex,
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        {filteredOptions.length === 0 ? (
          <div
            css={css`
              padding: 8px 12px;
              color: ${colors.NEUTRAL_50};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
            `}
          >
            No matching times
          </div>
        ) : (
          filteredOptions.map((option, idx) => {
            const isSelected = isTimeEqual(option.value, value)
            const isHighlighted = idx === highlightedIndex
            return (
              <div
                key={option.label}
                data-time-option
                ref={isSelected ? selectedItemRef : undefined}
                css={[
                  optionBaseStyles,
                  isSelected && optionSelectedStyles,
                  isHighlighted && !isSelected && optionHighlightedStyles,
                ]}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {option.label}
              </div>
            )
          })
        )}
      </div>,
      document.body
    )

  return (
    <>
      <input
        ref={inputRef}
        id={id}
        type='text'
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        css={inputStyles}
        className={className}
        style={style}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        autoComplete='off'
      />
      {dropdown}
    </>
  )
}

export default TimePickerWithInterval
